import React from "react";
import { Box, Button, Typography } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ImageIcon from '@mui/icons-material/Image';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { SvgIconComponent } from '@mui/icons-material';
import { IPortalCustomPageSectionItemResponse } from "../../model/base/response/portal-custom-page-response";

const FILE_TYPES: { extensions: string[]; icon: SvgIconComponent; color: string }[] = [
    { extensions: ['pdf'], icon: PictureAsPdfIcon, color: '#d32f2f' },
    { extensions: ['doc', 'docx', 'odt', 'rtf', 'txt'], icon: DescriptionIcon, color: '#1565c0' },
    { extensions: ['xls', 'xlsx', 'ods', 'csv'], icon: TableChartIcon, color: '#2e7d32' },
    { extensions: ['ppt', 'pptx', 'odp'], icon: SlideshowIcon, color: '#e65100' },
    { extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'], icon: ImageIcon, color: '#6a1b9a' },
    { extensions: ['zip', 'rar', '7z'], icon: FolderZipIcon, color: '#5d4037' },
];

/** Extensão pelo nome original enviado (ou, sem ele, pelo caminho no bucket). */
const fileExtension = (item: IPortalCustomPageSectionItemResponse) => {
    const name = item.fileOriginalName || item.file || '';
    const match = name.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : '';
};

/** Tamanho legível em pt-BR (ex.: "1,2 MB"). */
export const formatFileSize = (bytes: number | undefined) => {
    if (!bytes || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, exponent);
    return `${value.toLocaleString('pt-BR', { maximumFractionDigits: exponent === 0 ? 0 : 1 })} ${units[exponent]}`;
};

/** Seção "Documentos para Download": nome, identificação, descrição, tipo/tamanho do arquivo e botão de download. */
export function DocumentList({ items }: { items: IPortalCustomPageSectionItemResponse[] | undefined }) {
    const documents = (items || []).filter((item) => !!item.fileUrl);
    if (!documents.length) return null;

    return (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {documents.map((item) => {
                const extension = fileExtension(item);
                const type = FILE_TYPES.find((t) => t.extensions.includes(extension));
                const Icon = type?.icon || InsertDriveFileIcon;
                const meta = [extension.toUpperCase(), formatFileSize(item.fileSize)].filter(Boolean).join(' · ');

                return (
                    <Box
                        component="li"
                        key={item.id}
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            transition: 'border-color 0.2s ease',
                            '&:hover': { borderColor: 'primary.main' },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
                            <Icon sx={{ fontSize: 40, flexShrink: 0, color: type?.color || 'text.secondary' }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                                    {item.title}
                                </Typography>
                                {item.subtitle && (
                                    <Typography variant="body2" color="text.secondary">{item.subtitle}</Typography>
                                )}
                                {item.description && (
                                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{item.description}</Typography>
                                )}
                                {meta && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {meta}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            href={item.fileUrl!}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={item.fileOriginalName || true}
                            aria-label={`Baixar ${item.title}${meta ? ` (${meta})` : ''}`}
                            sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
                        >
                            Baixar
                        </Button>
                    </Box>
                );
            })}
        </Box>
    );
}
